import { ReactNode } from 'react'
import { Info } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type MetadataDisplayProps = {
  metadata: Record<string, unknown>
}

export const MetadataDisplay = ({ metadata }: MetadataDisplayProps) => {
  if (!metadata || Object.keys(metadata).length === 0) {
    return <span className="text-muted-foreground italic">No metadata</span>
  }

  return (
    <div className="space-y-1 max-w-md">
      {Object.entries(metadata).map(([key, value], index) => {
        const formattedKey = key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())

        let displayValue: ReactNode

        if (typeof value === 'boolean') {
          displayValue = value ? (
            <Badge variant="default" className="px-1.5 py-0.5 text-xs">
              Yes
            </Badge>
          ) : (
            <Badge variant="destructive" className="px-1.5 py-0.5 text-xs">
              No
            </Badge>
          )
        } else if (value === null || value === undefined) {
          displayValue = (
            <span className="text-muted-foreground italic">None</span>
          )
        } else if (typeof value === 'object') {
          try {
            const jsonString = JSON.stringify(value)
            displayValue = (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center">
                      <span className="text-xs font-mono bg-muted rounded px-1 py-0.5 break-all truncate max-w-[200px]">
                        {jsonString}
                      </span>
                      <Info className="h-3 w-3 ml-1 text-muted-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[400px] p-2 text-xs"
                  >
                    <pre className="whitespace-pre-wrap break-all">
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          } catch {
            displayValue = (
              <span className="text-xs font-mono">[Complex Object]</span>
            )
          }
        } else {
          displayValue = <span>{String(value)}</span>
        }

        return (
          <div key={index} className="flex flex-col text-sm">
            <span className="font-medium text-muted-foreground">
              {formattedKey}
            </span>
            {displayValue}
          </div>
        )
      })}
    </div>
  )
}
